import { Box, Button, Paper, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import progress from "../../../assets/images/progress.svg";
import theme from "../../../theme";
const steps = [
  { label: "Claim created", key: "DRAFT" }, //claim create
  { label: "Claim submitted", key: "ASSESSED" }, //consultant
  { label: "Reviewed By Admin", key: "NEW" }, // admin
  { label: "Reviewed By Manager", key: "REVIEWED" }, //claim manager
  { label: "Reviewed By Underwriter", key: "APPROVED" }, //underwriter
  { label: "Claim Processed", key: "PAID" }, //finance
];

type Props = {
  claimStatusData: {
    status:
      | "DRAFT"
      | "ASSESSED"
      | "NEW"
      | "REVIEWED"
      | "APPROVED"
      | "PAID"
      | "REJECTED";
    message: string;
    claimId: string;
    rejectedBy?: "admin" | "underwriter" | "manager";
  };
  handleGoHome: () => void;
};

export default function ClaimProgress({
  claimStatusData,
  handleGoHome,
}: Props) {
  const getRejectedStepKey = () => {
    if (claimStatusData.status === "REJECTED") {
      if (claimStatusData.rejectedBy === "manager") {
        return "REVIEWED";
      } else if (claimStatusData.rejectedBy === "underwriter") {
        return "APPROVED";
      }

      if (claimStatusData.message?.toLowerCase().includes("manager")) {
        return "REVIEWED";
      } else if (
        claimStatusData.message?.toLowerCase().includes("underwriter")
      ) {
        return "APPROVED";
      }

      return "NEW";
    }
    return claimStatusData.status;
  };

  const rejectedStepKey = getRejectedStepKey();
  const currentStatusIndex = steps.findIndex(
    (step) => step.key === rejectedStepKey
  );

  // Determine the active progress step (one step ahead of completed)
  const progressStepIndex = currentStatusIndex + 1;
  const showProgress =
    progressStepIndex < steps.length &&
    claimStatusData.status !== "REJECTED" &&
    claimStatusData.status !== "PAID";

  return (
    <Box px={15} py={6} sx={{ px: 6 }}>
      <Paper
        elevation={2}
        sx={{
          borderRadius: 3,
          padding: 3,
          margin: "auto",
          background: "#ffffff",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={3}
          sx={{ flexDirection: { xs: "column", md: "row" } }}
        >
          {steps.map((step, index) => {
            const isCompleted = index <= currentStatusIndex;
            const isProgressStep = showProgress && index === progressStepIndex;
            const isRejected =
              index === currentStatusIndex &&
              claimStatusData.status === "REJECTED";
            const isPaid = index === currentStatusIndex && step.key === "PAID";

            let StepIcon;

            if (isProgressStep) {
              // Show progress icon on the next step after completed
              StepIcon = (
                <img
                  src={progress}
                  alt="loading"
                  style={{ height: "30px", width: "30px" }}
                />
              );
            } else if (isCompleted) {
              if (isRejected) {
                StepIcon = (
                  <HighlightOffIcon
                    sx={{ color: "#D32F2F", fontSize: "34px" }}
                  />
                );
              } else if (isPaid) {
                StepIcon = (
                  <CheckCircleIcon
                    sx={{ color: "#009252", fontSize: "34px" }}
                  />
                );
              } else {
                StepIcon = (
                  <CheckCircleIcon
                    sx={{ color: "#EC6E00", fontSize: "34px" }}
                  />
                );
              }
            } else {
              StepIcon = (
                <CheckCircleIcon sx={{ color: "#DFDDDD", fontSize: "34px" }} />
              );
            }

            return (
              <Box
                key={step.key}
                textAlign="center"
                flex={1}
                position="relative"
              >
                <Box
                  position="relative"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height={80}
                >
                  {index !== steps.length - 1 && (
                    <Box
                      position="absolute"
                      top="50%"
                      left="55%"
                      width="90%"
                      height={4}
                      bgcolor={isCompleted ? "#EC6E00" : "#DFDDDD"}
                      sx={{
                        top: { xs: "70%", md: "50%" },
                        left: { xs: "50%", md: "55%" },
                        width: { xs: "4px", md: "90%" },
                        height: { xs: "110%", md: "4px" },
                        bgcolor: isCompleted ? "#EC6E00" : "#DFDDDD",
                        transform: {
                          xs: "translateX(-50%)",
                          md: "translateY(-50%)",
                        },
                        zIndex: 0,
                      }}
                    />
                  )}
                  <Box
                    width={40}
                    height={40}
                    zIndex={1}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    position="relative"
                  >
                    {StepIcon}
                  </Box>
                </Box>
                <Typography
                  mt={1}
                  fontSize={14}
                  color={isRejected ? "#D32F2F" : "#333"}
                  sx={{
                    ml: { xs: 18, md: 0 },
                    mt: { xs: -2.5 },
                    fontSize: { xs: "10px" },
                  }}
                >
                  {step.label}
                </Typography>
                {isRejected && (
                  <Typography fontSize={12} color="#D32F2F" fontStyle="italic">
                    Rejected
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      </Paper>
      <Box mt={4} display="flex" justifyContent="center">
        <Button
          variant="contained"
          sx={{
            backgroundColor: theme.palette.primary.main,
            borderRadius: "20px",
            px: 4,
            py: 1,
            textTransform: "none",
            width: "190px",
            height: "40px",
          }}
          onClick={handleGoHome}
        >
          Home
        </Button>
      </Box>
    </Box>
  );
}
